import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../layouts/AdminLayout';
import axiosInstance from '../../lib/axiosInstance';
import { toast } from 'react-toastify';
import { MdChevronRight, MdArrowBack, MdMessage, MdTopic } from 'react-icons/md';

const RaiseNewTicket = () => {
    const navigate = useNavigate();
    const [topics, setTopics] = useState([]);
    const [currentLevel, setCurrentLevel] = useState([]);
    const [selectionPath, setSelectionPath] = useState([]);
    const [isLeaf, setIsLeaf] = useState(false);
    const [loading, setLoading] = useState(true);
    const [raising, setRaising] = useState(false);
    const [comment, setComment] = useState('');
    const [priority, setPriority] = useState('medium');

    const fetchTopics = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/brand/support/topics');
            if (response.data.success) {
                setTopics(response.data.topics);
                setCurrentLevel(response.data.topics);
            }
        } catch (error) {
            console.error('Error fetching topics:', error);
            // toast.error('Failed to load support categories');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTopics();
    }, []);

    const handleTopicSelection = (topic) => {
        const newPath = [...selectionPath, { id: topic.id, label: topic.label }];
        setSelectionPath(newPath);

        if (topic.input_type === 'leaf') {
            setIsLeaf(true);
            setComment(topic.prefilled_text || '');
        } else {
            setCurrentLevel(topic.children || []);
        }
    };

    const handleBack = () => {
        if (selectionPath.length === 0) {
            navigate('/support');
            return;
        }

        const newPath = selectionPath.slice(0, -1);
        setSelectionPath(newPath);
        setIsLeaf(false);
        setComment('');

        // Recalculate current level
        let level = topics;
        for (const pathItem of newPath) {
            const found = level.find(t => t.id === pathItem.id);
            if (found) level = found.children || [];
        }
        setCurrentLevel(level);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!comment.trim()) return;

        try {
            setRaising(true);
            const leaf_topic_id = selectionPath[selectionPath.length - 1].id;
            const response = await axiosInstance.post('/brand/support/tickets', {
                leaf_topic_id,
                topic_path: selectionPath,
                priority,
                comment
            });
            if (response.data.success) {
                toast.success('Support ticket raised successfully');
                navigate(`/support/details/${response.data.ticket_no}`);
            }
        } catch (error) {
            console.error('Error raising ticket:', error);
            toast.error(error.response?.data?.message || 'Failed to raise ticket');
        } finally {
            setRaising(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-8">
                <button 
                    onClick={handleBack}
                    className="flex md:items-center gap-2 text-[var(--muted)] hover:text-[var(--text)] transition font-black uppercase tracking-widest text-[10px] mb-8"
                >
                    <MdArrowBack size={16}/> Go Back
                </button>

                <div className="mb-12">
                   <h1 className="text-4xl md:text-5xl font-black text-[var(--text)] tracking-tighter leading-none">Support Wizard</h1>
                   <p className="text-[var(--muted)] font-medium mt-2">Follow the path to raise your inquiry.</p>
                   {selectionPath.length > 0 && (
                       <div className="flex flex-wrap gap-2 mt-6">
                           {selectionPath.map((p, idx) => (
                               <div key={idx} className="flex items-center gap-2">
                                   <span className="bg-blue-600/10 text-blue-500 px-4 py-2 rounded-2xl text-xs font-black uppercase tracking-tighter border border-blue-500/20">
                                       {p.label}
                                   </span>
                                   {idx < selectionPath.length - 1 && <span className="text-[var(--border)] font-black">/</span>}
                               </div>
                           ))}
                       </div>
                   )}
                </div>

                {loading ? (
                    <div className="p-20 text-center animate-pulse text-[var(--muted)] font-black uppercase tracking-widest">Loading categories...</div>
                ) : !isLeaf ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {currentLevel.map((topic) => (
                            <button
                                key={topic.id}
                                onClick={() => handleTopicSelection(topic)}
                                className="group bg-[var(--surface)] border border-[var(--border)] hover:border-blue-500/30 p-8 rounded-[40px] text-left hover:shadow-xl hover:shadow-blue-500/5 transition-all flex items-center justify-between"
                            >
                                <div className="flex items-center gap-5">
                                    <div className="w-16 h-16 bg-[var(--bg)] rounded-[28px] flex items-center justify-center text-[var(--muted)] group-hover:bg-blue-600 group-hover:text-white transition-colors duration-500">
                                        <MdTopic size={32}/>
                                    </div>
                                    <span className="text-xl font-black text-[var(--text)] tracking-tighter group-hover:text-blue-600 transition-colors">{topic.label}</span>
                                </div>
                                <MdChevronRight size={24} className="text-[var(--border)] group-hover:text-blue-500 transition"/>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="bg-[var(--surface)] border border-[var(--border)] p-10 rounded-[48px] shadow-xl shadow-slate-200/30 dark:shadow-blue-900/5 animate-in fade-in zoom-in duration-300">
                        <form onSubmit={handleSubmit} className="space-y-8">
                            <div>
                                <label className="text-[10px] font-black tracking-widest text-[var(--muted)] uppercase mb-3 block">Describe your issue</label>
                                <textarea
                                    className="w-full bg-[var(--bg)] border-2 border-transparent focus:bg-[var(--surface)] focus:border-blue-500 p-6 rounded-[32px] text-sm h-48 outline-none transition-all font-medium leading-relaxed text-[var(--text)]"
                                    required
                                    placeholder="Provide details for faster resolution..."
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                />
                                <div className="flex items-center gap-2 mt-4 text-[10px] font-bold text-[var(--muted)] uppercase tracking-widest">
                                    <MdMessage/> Use standard professional language.
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="text-[10px] font-black tracking-widest text-[var(--muted)] uppercase mb-3 block text-opacity-50">Urgency Level</label>
                                    <div className="flex gap-2">
                                        {['low', 'medium', 'high'].map(p => (
                                            <button
                                                key={p}
                                                type="button"
                                                onClick={() => setPriority(p)}
                                                className={`flex-1 py-3 px-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                                    priority === p 
                                                    ? 'bg-[var(--text)] text-[var(--bg)] shadow-lg shadow-blue-500/10 scale-105' 
                                                    : 'bg-[var(--bg)] text-[var(--muted)] hover:bg-[var(--surface)] border border-transparent hover:border-[var(--border)]'
                                                }`}
                                            >
                                                {p}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-end">
                                    <button
                                        type="submit"
                                        disabled={raising || !comment.trim()}
                                        className="w-full bg-blue-600 text-white py-5 rounded-[28px] font-black shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-all transform hover:-translate-y-1 active:translate-y-0 disabled:opacity-50"
                                    >
                                        {raising ? 'Registering Ticket...' : 'Raising Inquiry Now'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}
            </div>
    );
};

export default RaiseNewTicket;
